import { RefObject, useEffect, useRef } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  CHAT_OAUTH_CONNECT_FAILED_MESSAGE,
  ChatNotificationPreferences,
  ChatOAuthChannel,
  ChatOAuthDialogState,
} from './settingsTypes';

interface UseChatOAuthReturnOptions {
  searchParams: URLSearchParams;
  navigate: NavigateFunction;
  chatAlertsSectionRef: RefObject<HTMLDivElement | null>;
  setActiveTab: (tab: number) => void;
  setChatPrefs: React.Dispatch<React.SetStateAction<ChatNotificationPreferences>>;
  fetchChatNotificationPreferences: () => Promise<void>;
  setChatOAuthDialog: React.Dispatch<React.SetStateAction<ChatOAuthDialogState | null>>;
}

const OAUTH_SUCCESS_MESSAGES: Record<ChatOAuthChannel, string> = {
  google:
    'Google Chat is connected. Chat alerts stay enabled — review timing below and save if you changed anything else.',
  teams:
    'Microsoft Teams is connected. Chat alerts stay enabled — review timing below and save if you changed anything else.',
  slack:
    'Slack is connected. Chat alerts stay enabled — review timing below and save if you changed anything else.',
};

const OAUTH_QUERY_KEYS: Record<ChatOAuthChannel, string> = {
  google: 'googleChat',
  teams: 'teamsChat',
  slack: 'slackChat',
};

const OAUTH_ENABLED_KEY: Record<
  ChatOAuthChannel,
  keyof Pick<
    ChatNotificationPreferences,
    'googleChatEnabled' | 'teamsChatEnabled' | 'slackChatEnabled'
  >
> = {
  google: 'googleChatEnabled',
  teams: 'teamsChatEnabled',
  slack: 'slackChatEnabled',
};

function getOAuthChannelFromParams(
  searchParams: URLSearchParams,
): { channel: ChatOAuthChannel; result: string } | null {
  for (const channel of ['google', 'teams', 'slack'] as const) {
    const result = searchParams.get(OAUTH_QUERY_KEYS[channel]);
    if (result) {
      return { channel, result };
    }
  }
  return null;
}

export function useChatOAuthReturn({
  searchParams,
  navigate,
  chatAlertsSectionRef,
  setActiveTab,
  setChatPrefs,
  fetchChatNotificationPreferences,
  setChatOAuthDialog,
}: UseChatOAuthReturnOptions) {
  const oauthReturnHandledRef = useRef(false);

  useEffect(() => {
    const oauthReturn = getOAuthChannelFromParams(searchParams);
    if (!oauthReturn || oauthReturnHandledRef.current) {
      return;
    }
    oauthReturnHandledRef.current = true;

    const { channel, result } = oauthReturn;
    const enabledKey = OAUTH_ENABLED_KEY[channel];

    setActiveTab(0);
    window.setTimeout(() => {
      chatAlertsSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 300);

    setChatPrefs((prev) => ({ ...prev, [enabledKey]: true }));

    if (result === 'connected') {
      setChatOAuthDialog({
        open: true,
        severity: 'success',
        channel,
        message: OAUTH_SUCCESS_MESSAGES[channel],
      });
      void fetchChatNotificationPreferences().then(() => {
        setChatPrefs((prev) => ({ ...prev, [enabledKey]: true }));
      });
    } else if (result === 'error') {
      setChatOAuthDialog({
        open: true,
        severity: 'error',
        channel,
        message: CHAT_OAUTH_CONNECT_FAILED_MESSAGE[channel],
      });
    }

    navigate('/settings', { replace: true });
  }, [
    searchParams,
    navigate,
    chatAlertsSectionRef,
    setActiveTab,
    setChatPrefs,
    fetchChatNotificationPreferences,
    setChatOAuthDialog,
  ]);
}
